import { useState, useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StarIcon from '@mui/icons-material/Star';

import { useAsignarTicket, useFetchAgentesMiDepartamento, useFetchUsuariosPorArea } from '@/shared/actions/erp/tickets-soporte-cliente';
import type { IUsuarioERP } from '@/shared/interfaces/erp/tickets-soporte-clientes';
import type { IUsuarioArea } from '@/shared/actions/erp/tickets-soporte-cliente/gestion-accesos.actions';

type ModalAsignarProps = {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  areaDestinoId?: string;
  areaDestinoNombre?: string;
  usuarioAsignadoActual?: string;
};

type UsuarioOption = {
  id: number;
  username: string;
  nombre_completo: string;
  email?: string;
  area?: string;
  es_responsable?: boolean;
};

export default function ModalAsignar({
  open, onClose, ticketId, areaDestinoId, areaDestinoNombre, usuarioAsignadoActual,
}: ModalAsignarProps) {
  const [selectedUser, setSelectedUser] = useState<UsuarioOption | null>(null);
  const [userSearch, setUserSearch] = useState('');

  const isReasignar = !!usuarioAsignadoActual;

  // Usuarios del área del ticket
  const { data: areaUsersRes, isLoading: isLoadingAreaUsers } = useFetchUsuariosPorArea(
    areaDestinoId || '',
    open && !!areaDestinoId
  );
  const areaUsers: IUsuarioArea[] = (areaUsersRes as any)?.data?.data || (areaUsersRes as any)?.data || [];

  // Busqueda de AGENTES del departamento del supervisor. El backend
  // filtra solo profile.rol=AGENTE del mismo departamento del usuario
  // logueado. Se dispara siempre que el modal esta abierto; el texto de
  // busqueda solo refina el filtro local del backend (username/nombre/email).
  const { data: usersRes, isLoading: isLoadingSearch } = useFetchAgentesMiDepartamento(
    userSearch,
    open,
  );
  const searchUsers: IUsuarioERP[] = (usersRes as any)?.data?.data || (usersRes as any)?.data || [];
  const shouldSearch = searchUsers.length > 0;

  // Opciones: si hay búsqueda activa, mostrar resultados de búsqueda; si no, mostrar usuarios del área
  const options: UsuarioOption[] = useMemo(() => {
    if (shouldSearch && searchUsers.length > 0) {
      // Resultados de búsqueda - marcar los que son del área
      const areaUserIds = new Set(areaUsers.map((u) => u.id));
      return searchUsers.map((u) => ({
        id: u.id,
        username: u.username,
        nombre_completo: u.nombre_completo,
        email: u.email,
        area: u.area,
        es_responsable: areaUserIds.has(u.id) ? areaUsers.find((au) => au.id === u.id)?.es_responsable : undefined,
      }));
    }

    // Sin búsqueda: mostrar usuarios del área
    return areaUsers.map((u) => ({
      id: u.id,
      username: u.username,
      nombre_completo: u.nombre_completo,
      area: u.area_nombre,
      es_responsable: u.es_responsable,
    }));
  }, [areaUsers, searchUsers, shouldSearch]);

  const asignarTicket = useAsignarTicket();

  const handleSubmit = () => {
    if (!selectedUser) return;
    asignarTicket.mutate(
      {
        id: ticketId,
        data: {
          usuario_asignado_id: selectedUser.id,
          usuario_asignado_nombre: selectedUser.nombre_completo,
        },
      },
      { onSuccess: () => handleClose() }
    );
  };

  const handleClose = () => {
    setSelectedUser(null);
    setUserSearch('');
    onClose();
  };

  const isLoading = isLoadingAreaUsers || (shouldSearch && isLoadingSearch);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isReasignar ? 'Reasignar Ticket' : 'Asignar Ticket'}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isReasignar && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Asignado actualmente a: <strong>{usuarioAsignadoActual}</strong>
            </Alert>
          )}

          {areaDestinoNombre && !shouldSearch && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Usuarios del área: <strong>{areaDestinoNombre}</strong>.
              Escriba para buscar en todas las áreas.
            </Alert>
          )}

          <Autocomplete
            options={options}
            getOptionLabel={(o) => `${o.nombre_completo} (${o.username})`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={selectedUser}
            onChange={(_, v) => setSelectedUser(v)}
            inputValue={userSearch}
            onInputChange={(_, v, reason) => {
              if (reason === 'input') setUserSearch(v);
              else if (reason === 'clear') setUserSearch('');
              else if (reason === 'reset') setUserSearch(v);
            }}
            loading={isLoading}
            noOptionsText={
              !areaDestinoId && userSearch.length < 2
                ? 'Escriba para buscar usuario'
                : isLoading
                  ? 'Cargando...'
                  : 'Sin resultados'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar Usuario"
                size="small"
                placeholder={areaDestinoId ? 'Buscar por nombre...' : 'Nombre, username o email...'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <Stack sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {option.nombre_completo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.username}
                      {option.email ? ` | ${option.email}` : ''}
                      {option.area ? ` | ${option.area}` : ''}
                    </Typography>
                  </Stack>
                  {option.es_responsable && (
                    <Chip
                      icon={<StarIcon />}
                      label="Supervisor"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            )}
          />

          {shouldSearch && !isLoading && options.length === 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              No se encontró ningún usuario con ese criterio.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedUser || asignarTicket.isPending}
        >
          {asignarTicket.isPending ? (isReasignar ? 'Reasignando...' : 'Asignando...') : (isReasignar ? 'Reasignar' : 'Asignar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
